import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { TrendingUp, Target, Shield, Sunset, Calculator, Globe, ArrowRight, Check, ChevronDown } from 'lucide-react';
import { services } from '../data/mockData';
import { Link } from 'react-router-dom';

const iconMap = {
  TrendingUp,
  Target,
  Shield,
  Sunset,
  Calculator,
  Globe
};

const serviceLinks = {
  1: '/wealth-builder',
  2: '/goal-planner',
  3: '/insurance',
  4: '/retirement',
  5: '/tax-optimization',
  6: '/nri'
};

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);

  const detailedServices = [
    {
      ...services[0],
      features: [
        'Personalized wealth growth strategies',
        'Tax-efficient investment planning',
        'Regular portfolio rebalancing',
        'Dedicated relationship manager'
      ],
      details: 'Our Corpus Builder program is designed for individuals who want to create long-term wealth through disciplined investing. We analyze your financial situation, risk tolerance, and goals to create a customized portfolio.'
    },
    {
      ...services[1],
      features: [
        'Life milestone planning',
        'Child education funding',
        'Marriage expense planning',
        'Custom SIP strategies'
      ],
      details: 'Goal Planner helps you work backwards from your dreams to create actionable investment plans. Whether its your childs education, a dream home, or early retirement, we map out the path.'
    },
    {
      ...services[2],
      features: [
        'Term life insurance',
        'Health insurance planning',
        'Critical illness coverage',
        'Claim assistance support'
      ],
      details: 'Our Insurance Plans ensure your familys financial security is never compromised. We help you choose the right coverage amount and policy type based on your needs.'
    },
    {
      ...services[3],
      features: [
        'Retirement corpus calculation',
        'Pension plan optimization',
        'Post-retirement income planning',
        'Healthcare cost planning'
      ],
      details: 'Plan for a comfortable retirement with our comprehensive Retirement Planning service. We calculate your required corpus considering inflation, lifestyle, and healthcare needs.'
    },
    {
      ...services[4],
      features: [
        'ELSS mutual fund selection',
        '80C investment optimization',
        'Capital gains management',
        'Tax harvesting strategies'
      ],
      details: 'Maximize your post-tax returns with our Tax Optimization strategies. We help you save taxes legally while ensuring your investments align with your overall financial goals.'
    },
    {
      ...services[5],
      features: [
        'NRE/NRO account guidance',
        'DTAA benefit optimization',
        'Repatriation assistance',
        'Estate planning for NRIs'
      ],
      details: 'NRI Connect provides specialized services for Non-Resident Indians. We understand the complexities of cross-border investments and help you navigate regulatory requirements.'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/3 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="text-white/40 text-sm uppercase tracking-widest mb-4 block">OUR SERVICES</span>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6">
              How Frontier can
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">
                help you
              </span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed">
              Comprehensive financial solutions tailored to your unique goals.
              From wealth building to retirement planning, we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-6">
            {detailedServices.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isExpanded = expandedService === service.id;
              
              return (
                <div
                  key={service.id}
                  className="group rounded-3xl border border-gray-200 overflow-hidden transition-all duration-500 hover:shadow-xl"
                >
                  <div
                    className="p-8 cursor-pointer"
                    onClick={() => setExpandedService(isExpanded ? null : service.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className={`p-4 rounded-2xl bg-gradient-to-br ${service.color}`}>
                          {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-black">{service.title}</h3>
                          <p className="text-gray-500 mt-1">{service.description}</p>
                        </div>
                      </div>
                      <ChevronDown
                        className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-8 pb-8 pt-2 border-t border-gray-100">
                      <div className="grid lg:grid-cols-2 gap-8">
                        <div>
                          <h4 className="text-lg font-semibold text-black mb-4">Key Features</h4>
                          <ul className="space-y-3">
                            {service.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-3">
                                <div className="p-1 bg-emerald-100 rounded-full">
                                  <Check className="w-4 h-4 text-emerald-600" />
                                </div>
                                <span className="text-gray-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-black mb-4">About This Service</h4>
                          <p className="text-gray-600 leading-relaxed mb-6">{service.details}</p>
                          <div className="flex gap-4">
                            <Link
                              to={serviceLinks[service.id] || '/contact'}
                              className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white font-medium rounded-full hover:shadow-lg transition-all duration-300 group"
                            >
                              Learn More
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                            <Link
                              to="/contact"
                              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-black font-medium rounded-full hover:bg-gray-200 transition-all duration-300"
                            >
                              Get Started
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to start your investment journey?
          </h2>
          <p className="text-white/60 mb-10">
            Schedule a free consultation with our experts
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
